import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr, track } from "../api";
import { isNative, loginMobile } from "../mobileAuth";
import { useAuth } from "../AuthContext";
import { Logo, Page } from "../components/Layout";
import { Eye, EyeOff } from "lucide-react";

const PROFESSIONS = ["Tatuatore", "Tricopigmentista", "PMU Artist", "Onicotecnica", "Altro professionista"];

export default function AuthPage({ mode }) {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const roleParam = params.get("role") || "artist";
  const next = params.get("next") || "";
  const invite = params.get("invite") || localStorage.getItem("mas_ref_coworking") || "";
  useEffect(() => {
    const iv = params.get("invite");
    if (iv) localStorage.setItem("mas_ref_coworking", iv);
  }, [params]);
  const [tab, setTab] = useState(mode === "login" ? "login" : "register");
  const [role, setRole] = useState(roleParam);
  const [showInvite, setShowInvite] = useState(!!invite);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [f, setF] = useState({
    name: "", email: "", password: "", phone: "", profession: "Tatuatore",
    profession_other: "", privacy_consent: false, invite_code: invite,
  });
  useEffect(() => { track("registration_started", { role }); }, []); // eslint-disable-line

  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "login") {
        console.info("[mas-auth] login_start");
        let data;
        if (isNative()) {
          data = await loginMobile(f.email.trim(), f.password);
        } else {
          ({ data } = await api.post("/auth/login", { email: f.email.trim(), password: f.password }));
        }
        console.info("[mas-auth] auth_user_set");
        setUser(data);
        toast.success(`Bentornato, ${data.name}`);
        console.info("[mas-auth] navigation_start");
        nav(next || (data.role === "admin" ? "/admin" : "/profilo"));
        console.info("[mas-auth] login_complete");
        return;
      }
      if (!f.name.trim()) { toast.error("Inserisci il tuo nome"); setLoading(false); return; }
      if (!/^\S+@\S+\.\S+$/.test(f.email)) { toast.error("Inserisci un'email valida"); setLoading(false); return; }
      if (f.password.length < 6) { toast.error("La password deve avere almeno 6 caratteri"); setLoading(false); return; }
      if (role === "artist") {
        if (!f.phone.trim()) { toast.error("Inserisci il tuo numero di telefono"); setLoading(false); return; }
        if (!f.privacy_consent) { toast.error("Accetta la Privacy Policy per continuare"); setLoading(false); return; }
        const { data } = await api.post("/auth/register/artist", f);
        setUser(data);
        toast.success("Registrazione completata con successo.");
        nav(next || "/prenota");
      } else {
        const { data } = await api.post("/auth/register/customer", { name: f.name, email: f.email, password: f.password, phone: f.phone });
        setUser(data);
        toast.success("Registrazione completata con successo.");
        nav(next || "/profilo");
      }
    } catch (err) {
      if (tab === "login" && err?.response?.status === 429) {
        toast.error("Troppi tentativi di accesso. Attendi qualche minuto e riprova.");
      } else {
        toast.error(apiErr(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full border border-gray-200 rounded-xl px-4 py-3 mt-1 focus:border-violet-500 outline-none";

  return (
    <Page>
      <div className="text-center mb-6"><Logo className="inline-block" sub /></div>

      <div className="flex bg-gray-100 rounded-full p-1 mb-6 text-sm font-medium">
        <button data-testid="tab-register" onClick={() => setTab("register")}
          className={`flex-1 py-2 rounded-full ${tab === "register" ? "bg-white shadow-sm" : "text-gray-500"}`}>Registrati</button>
        <button data-testid="tab-login" onClick={() => setTab("login")}
          className={`flex-1 py-2 rounded-full ${tab === "login" ? "bg-white shadow-sm" : "text-gray-500"}`}>Accedi</button>
      </div>

      {tab === "register" && (
        <>
          <h1 className="serif text-2xl">Registrati gratis in meno di 1 minuto.</h1>
          <p className="text-sm text-gray-500 mt-1 mb-4">Nessun abbonamento. Paghi solo quando prenoti.</p>
          <div className="mb-5">
            {role === "artist" ? (
              <div className="flex items-center justify-between border border-violet-600 text-violet-700 rounded-full py-2.5 px-4 text-sm font-medium">
                <span data-testid="role-artist-active">Sono un artista</span>
                <button type="button" data-testid="switch-to-customer" onClick={() => setRole("customer")}
                  className="text-xs text-gray-400 font-normal underline">Sei un cliente?</button>
              </div>
            ) : (
              <div className="flex items-center justify-between border border-violet-600 text-violet-700 rounded-full py-2.5 px-4 text-sm font-medium">
                <span data-testid="role-customer-active">Sono un cliente</span>
                <button type="button" data-testid="switch-to-artist" onClick={() => setRole("artist")}
                  className="text-xs text-gray-400 font-normal underline">Sei un artista?</button>
              </div>
            )}
          </div>
        </>
      )}
      {tab === "login" && <h1 className="serif text-2xl mb-4">Accedi al tuo profilo</h1>}

      <form onSubmit={submit} className="space-y-3">
        {tab === "register" && (
          <label className="block text-sm">Nome
            <input data-testid="input-name" required className={input} value={f.name} onChange={set("name")} />
          </label>
        )}
        {tab === "register" && role === "artist" && (
          <>
            <label className="block text-sm">Professione
              <select data-testid="input-profession" className={input} value={f.profession} onChange={set("profession")}>
                {PROFESSIONS.map((p) => <option key={p}>{p}</option>)}
              </select>
            </label>
            {f.profession === "Altro professionista" && (
              <input data-testid="input-profession-other" placeholder="Specifica la professione" className={input} value={f.profession_other} onChange={set("profession_other")} />
            )}
          </>
        )}
        <label className="block text-sm">Email
          <input data-testid="input-email" type="email" required className={input} value={f.email} onChange={set("email")} autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete={tab === "login" ? "username" : "email"} />
        </label>
        <label className="block text-sm">{tab === "register" ? "Crea una password" : "Password"}
          <div className="relative">
            <input data-testid="input-password" type={showPw ? "text" : "password"} required minLength={6} className={`${input} pr-12`} value={f.password} onChange={set("password")} autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete={tab === "login" ? "current-password" : "new-password"} />
            <button
              type="button"
              data-testid="toggle-password-visibility"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? "Nascondi password" : "Mostra password"}
              title={showPw ? "Nascondi password" : "Mostra password"}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center text-gray-400 hover:text-violet-600 focus:outline-none"
            >
              {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {tab === "register" && <span className="block text-xs text-gray-400 mt-1">Servirà per accedere al tuo account MAS.</span>}
        </label>
        {tab === "login" && (
          <div className="text-right -mt-2">
            <Link to="/password-dimenticata" data-testid="forgot-password-link" className="text-xs text-violet-700 font-medium">Password dimenticata?</Link>
          </div>
        )}
        {tab === "register" && role === "artist" && (
          <label className="block text-sm">Telefono
            <input data-testid="input-phone" required className={input} value={f.phone} onChange={set("phone")} />
          </label>
        )}

        {tab === "register" && role === "artist" && (
          <>
            {!showInvite ? (
              <button type="button" data-testid="toggle-invite" onClick={() => setShowInvite(true)}
                className="text-sm text-violet-700 font-medium">Hai un codice invito?</button>
            ) : (
              <input data-testid="input-invite" placeholder="Codice invito" className={input} value={f.invite_code} onChange={set("invite_code")} />
            )}
            <label className="flex items-start gap-2 text-xs text-gray-600 pt-1">
              <input data-testid="input-privacy" type="checkbox" className="mt-0.5" checked={f.privacy_consent} onChange={set("privacy_consent")} />
              Accetto la Privacy Policy e i Termini di utilizzo (GDPR).
            </label>
          </>
        )}

        <button data-testid="submit-auth" disabled={loading} className="btn-primary w-full mt-2">
          {loading ? "Attendere…" : tab === "login" ? "Accedi" : "Registrati gratis"}
        </button>
      </form>

      <p className="text-center text-xs text-gray-400 mt-6">
        Preferisci? <Link to="/" className="underline">Torna alla Home</Link>
      </p>
      <p className="text-center text-[11px] text-gray-400 mt-3 space-x-2">
        <Link to="/privacy" className="underline" data-testid="auth-link-privacy">Privacy Policy</Link>
        <span>·</span>
        <Link to="/termini" className="underline" data-testid="auth-link-termini">Termini di Servizio</Link>
      </p>
    </Page>
  );
}
